import { Image, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import Dropzone from 'react-dropzone'

import { Button, DefaultSheet, DefaultTooltip, Progress, SheetBody, cn } from 'twyne-new.ui'

import { useImages } from '@/components/builder/utils/tailwindConfig/hooks'
import Images, { uploadImage } from '@/modules/cms/views/post/components/BuilderConfig/Images'

const formatSize = bytes => bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`

const truncateName = (name = '', max = 20) => {
  if (name.length <= max) return name
  const dot = name.lastIndexOf('.')
  const ext = dot > 0 ? name.slice(dot) : ''
  const base = name.slice(0, name.length - ext.length)
  const tail = base.slice(-4)
  const head = base.slice(0, Math.max(4, max - ext.length - tail.length - 1))
  return `${head}…${tail}${ext}`
}

const fileType = (name = '') => {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : ''
}

function Thumb({ src, onLoad }) {
  return <div className='size-12 shrink-0 rounded-sm twy-bg-base-neutral twy-text-base-neutral flex items-center justify-center overflow-hidden'>
    {src ? <img src={src} alt='' className='max-h-full max-w-full object-contain' onLoad={onLoad} /> : <Image size={20} />}
  </div>
}

function Box({ image, upload, onRemove }) {
  const [dimensions, setDimensions] = useState(null)

  useEffect(() => setDimensions(null), [image?.src])

  const handleLoad = e => setDimensions(`${e.target.naturalWidth} × ${e.target.naturalHeight}`)

  if (upload) {
    return <>
      <Thumb src={upload.preview} />
      <div className='min-w-0 flex-1 flex flex-col gap-1.5'>
        <div className='flex items-center gap-2'>
          <span className='flex-1 twy-text-base-accent'>Uploading...</span>
          <span className='twy-text-base-neutral'>{upload.progress}%</span>
        </div>
        <Progress value={upload.progress} color='accent' />
      </div>
    </>
  }

  if (image?.src) {
    const meta = [fileType(image.name), dimensions, image.bytes && formatSize(image.bytes)].filter(Boolean)

    return <>
      <Thumb src={image.src} onLoad={handleLoad} />
      <div className='min-w-0 flex-1 flex flex-col gap-0.5'>
        <span className='truncate twy-text-base-accent' title={image.name}>{truncateName(image.name)}</span>
        <span className='truncate twy-text-base-neutral'>{meta.join(' · ')}</span>
      </div>
      <DefaultTooltip content='Remove image' triggerProps={{ asChild: true }}>
        <Button size='sm' variant='ghost' color='danger' onClick={e => { e.stopPropagation(); onRemove() }}>
          <Trash2 size={14} />
        </Button>
      </DefaultTooltip>
    </>
  }

  return null
}

function Trigger({ image, upload, onDrop, onRemove }) {
  return <Dropzone
    multiple={false}
    accept='image/*'
    disableClick
    style={{}}
    className='w-full'
    onDrop={files => !upload && files[0] && onDrop(files[0])}
  >
    {({ isDragActive }) => <div className={cn(
      'flex items-center gap-3 p-2 rounded-sm border border-dashed twy-border-base twy-bg-base cursor-pointer transition-colors hover:twy-text-accent hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10',
      isDragActive && 'twy-subtle-accent twy-text-accent border-blue-500 hover:border-blue-500 hover:bg-blue-100 dark:hover:bg-blue-500/30',
    )}>
      {image?.src || upload ? <Box image={image} upload={upload} onRemove={onRemove} /> : <>
        <Thumb />
        <div className='min-w-0 flex-1 flex flex-col gap-0.5'>
          <span>{isDragActive ? 'Drop to upload' : 'Select or drop image here'}</span>
          <span className='twy-text-base-neutral'>Any image file</span>
        </div>
      </>}
    </div>}
  </Dropzone>
}

export default function ImageField({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const [upload, setUpload] = useState(null)
  const [sizes, setSizes] = useState({})
  const { value: images, setValue: setImages } = useImages()

  const handleDrop = async file => {
    const preview = URL.createObjectURL(file)
    setUpload({ preview, progress: 0 })
    try {
      const image = await uploadImage(file, progress => setUpload({ preview, progress }))
      setImages([...Array.isArray(images) ? images : [], image])
      setSizes(prev => ({ ...prev, [image.src]: file.size }))
      onChange({ value: { src: image.src, name: image.name } })
    } finally {
      URL.revokeObjectURL(preview)
      setUpload(null)
    }
  }

  const image = value?.src ? { ...value, bytes: sizes[value.src] } : value

  return <DefaultSheet
    wrapperProps={{ open, onOpenChange: setOpen }}
    triggerProps={{ asChild: true }}
    trigger={<Trigger image={image} upload={upload} onDrop={handleDrop} onRemove={() => onChange({ value: { src: undefined, name: undefined } })} />}
    title='Select an image'
    contentProps={{ size: 'xl' }}
  >
    <SheetBody>
      <Images enableSelect onSelect={image => onChange({ value: image })} />
    </SheetBody>
  </DefaultSheet>
}
